import clsx from 'clsx'
import ImageUploading from 'react-images-uploading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getUrlFromAvatarImage } from 'components/common/AvatarImage'
import { AvatarEditorModal } from 'components/organisms/modals/avatar-editor-modal'
import { useAvatarUpload } from 'containers/avatar-upload/hooks/use-avatar-upload'

interface SeriesAvatarUploadProps {
	/** The current image (media-relative path), or '' for none. */
	value: string
	onChange: (avatarUrl: string) => void
	uploading: boolean
	setUploading: (uploading: boolean) => void
	disabled?: boolean
}

/**
 * Picks the series' image — the avatar of its hidden parent event, shown on the series pages and in its
 * invitations, emails and notifications. Drawn as the series' card stack (like SeriesStackIcon): the
 * image is the top card. Click or drop an image on it; uploads go through the event avatar flow.
 */
export function SeriesAvatarUpload({ value, onChange, uploading, setUploading, disabled }: SeriesAvatarUploadProps) {
	const {
		avatar,
		fileInput,
		openAvatarEditorModal,
		setOpenAvatarEditorModal,
		isLoading,
		isDisabled,
		handleImageUpload,
		handleAvatarSubmit,
		handleClear,
	} = useAvatarUpload({ isReadOnly: disabled, uploading, setUploading, defaultValue: value, onChange })

	return (
		<>
			<ImageUploading
				value={[]}
				onChange={handleImageUpload}
				dataURLKey="data_url"
				acceptType={['jpg', 'png', 'jpeg']}
			>
				{({ dragProps, isDragging, onImageUpload }) => (
					// The two cards behind the top one make the tile read as a series, not a single event.
					<div className="relative size-[96px] flex-none" {...dragProps}>
						<div
							aria-hidden="true"
							className="absolute left-3 top-0 size-[84px] rounded-xl border border-translucent-plus-border bg-white/[0.06]"
						/>
						<div aria-hidden="true" className="absolute left-1.5 top-1.5 size-[84px] rounded-xl bg-primary/40" />
						<button
							type="button"
							onClick={onImageUpload}
							disabled={isDisabled}
							aria-label={avatar ? 'Change series image' : 'Add series image'}
							className={clsx(
								'group absolute left-0 top-3 size-[84px] overflow-hidden rounded-xl transition-colors',
								'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-primary-border',
								'disabled:cursor-not-allowed',
								avatar
									? 'bg-primary-surface'
									: 'flex flex-col items-center justify-center gap-1 border border-dashed border-primary/70 bg-primary/15 text-primary-text hover:bg-primary/25',
								isDragging && 'ring-2 ring-primary'
							)}
						>
							{avatar ? (
								<>
									<img src={getUrlFromAvatarImage(avatar)} alt="" className="size-full object-cover" />
									{!isLoading && (
										<span className="absolute inset-0 flex items-center justify-center bg-black/55 text-xs font-semibold opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
											Change
										</span>
									)}
								</>
							) : (
								<>
									<FontAwesomeIcon icon={['fas', 'image']} className="text-lg" />
									<span className="text-xs font-semibold">{isDragging ? 'Drop here' : 'Add image'}</span>
								</>
							)}
							{isLoading && (
								<span className="absolute inset-0 flex items-center justify-center bg-black/60">
									<FontAwesomeIcon icon={['fas', 'spinner']} spin />
								</span>
							)}
						</button>
						{!!avatar && !isDisabled && (
							<button
								type="button"
								onClick={handleClear}
								aria-label="Remove series image"
								className="absolute left-[56px] top-4 flex size-6 items-center justify-center rounded-full bg-black/70 text-[11px] text-primary-text transition-colors hover:bg-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
							>
								<FontAwesomeIcon icon={['fas', 'xmark']} />
							</button>
						)}
					</div>
				)}
			</ImageUploading>

			<AvatarEditorModal
				open={openAvatarEditorModal}
				setOpen={setOpenAvatarEditorModal}
				fileInput={fileInput}
				viewBorderRadius={16}
				handleOnSubmit={handleAvatarSubmit}
				forceRatio={1}
			/>
		</>
	)
}
